/**
 * Board Service
 *
 * Handles all Family Board operations including:
 * - Creating, reading, updating, deleting posts
 * - Liking/unliking posts
 * - Adding comments to posts
 * - Real-time subscriptions to posts and comments
 *
 * Data structure: families/{familyCode}/posts/{postId}/
 */

import { db } from '../firebase';
import logger from '../utils/logger';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  limit,
  startAfter,
  arrayUnion,
  arrayRemove,
  increment,
  serverTimestamp
} from 'firebase/firestore';

class BoardService {
  /**
   * Subscribe to posts in real-time
   * Returns an unsubscribe function
   */
  subscribeToPosts(familyCode, callback, limitCount = 20) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const postsRef = collection(db, 'families', familyCode, 'posts');
    const q = query(
      postsRef,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const posts = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(posts);
      },
      (error) => {
        logger.error('Error subscribing to posts:', error);
        callback([]);
      }
    );
  }

  /**
   * Create a new post
   * @param {string} familyCode - Family code
   * @param {string} userId - Author's user ID
   * @param {object} authorData - Author information (name, photo)
   * @param {object} postData - Post content (text, media, visibility)
   * @returns {Promise<string>} - New post ID
   */
  async createPost(familyCode, userId, authorData, postData) {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const postsRef = collection(db, 'families', familyCode, 'posts');

    const post = {
      authorId: userId,
      authorName: authorData.displayName || `${authorData.firstName} ${authorData.lastName}`,
      authorPhoto: authorData.profilePictureUrl || '',

      // Content
      type: postData.photos?.length > 0 ? 'photo' : 'text',
      text: postData.text || '',
      media: postData.media || [],

      // Engagement
      likes: 0,
      likedBy: [],
      commentsCount: 0,
      sharesCount: 0,

      // Visibility
      visibility: postData.visibility || 'all',
      visibleToGroups: postData.visibleToGroups || [],
      visibleToUsers: postData.visibleToUsers || [],

      // Metadata
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isPinned: false,
      isArchived: false,

      // Optional fields
      location: postData.location || null,
      tags: postData.tags || [],
      mentionedUsers: postData.mentionedUsers || []
    };

    const docRef = await addDoc(postsRef, post);
    return docRef.id;
  }

  /**
   * Get a single post by ID
   */
  async getPost(familyCode, postId) {
    if (!familyCode || !postId) {
      throw new Error('Family code and post ID are required');
    }

    const postRef = doc(db, 'families', familyCode, 'posts', postId);
    const postSnap = await getDoc(postRef);

    if (!postSnap.exists()) {
      throw new Error('Post not found');
    }

    return {
      id: postSnap.id,
      ...postSnap.data()
    };
  }

  /**
   * Update a post
   * Only the author or admin can update
   */
  async updatePost(familyCode, postId, updates) {
    if (!familyCode || !postId) {
      throw new Error('Family code and post ID are required');
    }

    const postRef = doc(db, 'families', familyCode, 'posts', postId);

    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    // Remove undefined values
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(postRef, updateData);
  }

  /**
   * Delete a post and all its comments
   * Only the author or admin can delete
   */
  async deletePost(familyCode, postId) {
    if (!familyCode || !postId) {
      throw new Error('Family code and post ID are required');
    }

    // Delete all comments in subcollection first
    const commentsRef = collection(db, 'families', familyCode, 'posts', postId, 'comments');
    const commentsSnapshot = await getDocs(query(commentsRef));

    const deletePromises = commentsSnapshot.docs.map(commentDoc =>
      deleteDoc(doc(db, 'families', familyCode, 'posts', postId, 'comments', commentDoc.id))
    );
    await Promise.all(deletePromises);

    // Then delete the post document
    const postRef = doc(db, 'families', familyCode, 'posts', postId);
    await deleteDoc(postRef);
  }

  /**
   * Toggle like on a post
   * Adds/removes user from likedBy array and increments/decrements like count
   */
  async toggleLike(familyCode, postId, userId) {
    if (!familyCode || !postId || !userId) {
      throw new Error('Family code, post ID, and user ID are required');
    }

    const postRef = doc(db, 'families', familyCode, 'posts', postId);

    // Check if already liked
    const postSnap = await getDoc(postRef);
    if (!postSnap.exists()) {
      throw new Error('Post not found');
    }

    const isLiked = postSnap.data().likedBy?.includes(userId);

    await updateDoc(postRef, {
      likes: increment(isLiked ? -1 : 1),
      likedBy: isLiked ? arrayRemove(userId) : arrayUnion(userId),
      updatedAt: serverTimestamp()
    });

    return !isLiked; // Return new liked state
  }

  /**
   * Add or update a reaction on a post
   * Reactions are stored as a map: { [userId]: { type, userName, timestamp } }
   * If user already has a reaction and clicks same one, it's removed
   */
  async addReaction(familyCode, postId, userId, userName, reactionType) {
    if (!familyCode || !postId || !userId || !reactionType) {
      throw new Error('Family code, post ID, user ID, and reaction type are required');
    }

    const postRef = doc(db, 'families', familyCode, 'posts', postId);
    const postSnap = await getDoc(postRef);

    if (!postSnap.exists()) {
      throw new Error('Post not found');
    }

    const currentReactions = postSnap.data().reactions || {};
    const userCurrentReaction = currentReactions[userId];

    // If user clicked same reaction, remove it
    if (userCurrentReaction?.type === reactionType) {
      const { [userId]: removed, ...remainingReactions } = currentReactions;
      await updateDoc(postRef, {
        reactions: remainingReactions,
        updatedAt: serverTimestamp()
      });
      return null;
    }

    // Add or update reaction
    await updateDoc(postRef, {
      [`reactions.${userId}`]: {
        type: reactionType,
        userName: userName || 'Unknown',
        timestamp: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    });

    return reactionType;
  }

  /**
   * Get reaction counts grouped by type
   */
  getReactionCounts(reactions) {
    if (!reactions) return {};

    const counts = {};
    Object.values(reactions).forEach(reaction => {
      const type = reaction.type;
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }

  /**
   * Subscribe to comments for a post in real-time
   */
  subscribeToComments(familyCode, postId, callback) {
    if (!familyCode || !postId) {
      throw new Error('Family code and post ID are required');
    }

    const commentsRef = collection(db, 'families', familyCode, 'posts', postId, 'comments');
    const q = query(commentsRef, orderBy('createdAt', 'asc'));

    return onSnapshot(
      q,
      (snapshot) => {
        const comments = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(comments);
      },
      (error) => {
        logger.error('Error subscribing to comments:', error);
        callback([]);
      }
    );
  }

  /**
   * Add a comment to a post
   */
  async addComment(familyCode, postId, userId, authorData, text) {
    if (!familyCode || !postId || !userId || !text?.trim()) {
      throw new Error('Family code, post ID, user ID, and text are required');
    }

    const commentsRef = collection(db, 'families', familyCode, 'posts', postId, 'comments');

    const comment = {
      postId,
      authorId: userId,
      authorName: authorData.displayName || `${authorData.firstName} ${authorData.lastName}`,
      authorPhoto: authorData.profilePictureUrl || '',
      text: text.trim(),

      // Nested replies
      parentCommentId: null,

      // Engagement
      likes: 0,
      likedBy: [],

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isEdited: false
    };

    const docRef = await addDoc(commentsRef, comment);

    // Increment comment count on post
    const postRef = doc(db, 'families', familyCode, 'posts', postId);
    await updateDoc(postRef, {
      commentsCount: increment(1),
      updatedAt: serverTimestamp()
    });

    return docRef.id;
  }

  /**
   * Update a comment
   */
  async updateComment(familyCode, postId, commentId, text) {
    if (!familyCode || !postId || !commentId || !text?.trim()) {
      throw new Error('Family code, post ID, comment ID, and text are required');
    }

    const commentRef = doc(db, 'families', familyCode, 'posts', postId, 'comments', commentId);

    await updateDoc(commentRef, {
      text: text.trim(),
      updatedAt: serverTimestamp(),
      isEdited: true
    });
  }

  /**
   * Delete a comment
   */
  async deleteComment(familyCode, postId, commentId) {
    if (!familyCode || !postId || !commentId) {
      throw new Error('Family code, post ID, and comment ID are required');
    }

    const commentRef = doc(db, 'families', familyCode, 'posts', postId, 'comments', commentId);
    await deleteDoc(commentRef);

    // Decrement comment count on post
    const postRef = doc(db, 'families', familyCode, 'posts', postId);
    await updateDoc(postRef, {
      commentsCount: increment(-1),
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Save/bookmark a post for the current user
   */
  async savePost(familyCode, postId, userId) {
    if (!familyCode || !postId || !userId) {
      throw new Error('Family code, post ID, and user ID are required');
    }

    const savedRef = doc(db, 'users', userId, 'savedPosts', postId);
    const savedSnap = await getDoc(savedRef);

    if (savedSnap.exists()) {
      // Unsave
      await deleteDoc(savedRef);
      return false;
    } else {
      // Save
      await addDoc(collection(db, 'users', userId, 'savedPosts'), {
        postId,
        familyCode,
        savedAt: serverTimestamp()
      });
      return true;
    }
  }

  /**
   * Report a post for review
   */
  async reportPost(familyCode, postId, userId, reason) {
    if (!familyCode || !postId || !userId) {
      throw new Error('Family code, post ID, and user ID are required');
    }

    const reportsRef = collection(db, 'families', familyCode, 'reports');
    await addDoc(reportsRef, {
      postId,
      reportedBy: userId,
      reason: reason || 'Inappropriate content',
      status: 'pending',
      createdAt: serverTimestamp()
    });
  }

  /**
   * Toggle like on a comment
   */
  async toggleCommentLike(familyCode, postId, commentId, userId) {
    if (!familyCode || !postId || !commentId || !userId) {
      throw new Error('Family code, post ID, comment ID, and user ID are required');
    }

    const commentRef = doc(db, 'families', familyCode, 'posts', postId, 'comments', commentId);

    // Check if already liked
    const commentSnap = await getDoc(commentRef);
    if (!commentSnap.exists()) {
      throw new Error('Comment not found');
    }

    const isLiked = commentSnap.data().likedBy?.includes(userId);

    await updateDoc(commentRef, {
      likes: increment(isLiked ? -1 : 1),
      likedBy: isLiked ? arrayRemove(userId) : arrayUnion(userId),
      updatedAt: serverTimestamp()
    });

    return !isLiked;
  }

  /**
   * Pin/unpin a post (admin only)
   */
  async togglePin(familyCode, postId) {
    if (!familyCode || !postId) {
      throw new Error('Family code and post ID are required');
    }

    const postRef = doc(db, 'families', familyCode, 'posts', postId);
    const postSnap = await getDoc(postRef);

    if (!postSnap.exists()) {
      throw new Error('Post not found');
    }

    const isPinned = postSnap.data().isPinned;

    await updateDoc(postRef, {
      isPinned: !isPinned,
      updatedAt: serverTimestamp()
    });

    return !isPinned;
  }

  /**
   * Archive/unarchive a post
   */
  async toggleArchive(familyCode, postId) {
    if (!familyCode || !postId) {
      throw new Error('Family code and post ID are required');
    }

    const postRef = doc(db, 'families', familyCode, 'posts', postId);
    const postSnap = await getDoc(postRef);

    if (!postSnap.exists()) {
      throw new Error('Post not found');
    }

    const isArchived = postSnap.data().isArchived;

    await updateDoc(postRef, {
      isArchived: !isArchived,
      updatedAt: serverTimestamp()
    });

    return !isArchived;
  }

  /**
   * Load more posts (pagination)
   */
  async loadMorePosts(familyCode, lastPostId, limitCount = 10) {
    if (!familyCode || !lastPostId) {
      throw new Error('Family code and last post ID are required');
    }

    const postsRef = collection(db, 'families', familyCode, 'posts');
    const lastPostRef = doc(postsRef, lastPostId);
    const lastPostSnap = await getDoc(lastPostRef);

    if (!lastPostSnap.exists()) {
      throw new Error('Last post not found');
    }

    const q = query(
      postsRef,
      orderBy('createdAt', 'desc'),
      startAfter(lastPostSnap),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
}

export const boardService = new BoardService();
export default boardService;
